import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FrameSizeSelectorComponent } from '@nrwl/test-setup';
import { MatDialog } from '@angular/material/dialog';
import { CommonMaterialModule } from '@nrwl/common-material';
import { state, style, transition, trigger, animate } from '@angular/animations';
import { FormsModule } from '@angular/forms';

import { FormatService, TestMode } from '@nrwl/common-lib';
import { RFC2544SetupService } from '../../rfc2544-setup.service';
import { MatTableDataSource } from '@angular/material/table';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  REMOVED
} from '@nrwl/test-setup';
import {
  RFC2544ConfigFieldMapping as ConfigFieldMapping,
  CONFIRM_SELECTION,
  DSCP_CONFIG_TYPE,
  IPAddressValue,
  IPV4_TABLE_HEADERS,
  IPV6_TABLE_HEADERS,
  TOS_DIFF_SERV_SELECTION,
  vlanConfigValue
} from '../../models/models';
import { Subject, Subscription } from 'rxjs';
import { isNil, cloneDeep } from 'lodash';
import { IPv4 } from 'subnet-check';
@Component({
  selector: 'nrwl-rfc2544-streams',
  standalone: true,
  imports: [CommonModule, FrameSizeSelectorComponent, CommonMaterialModule, FormsModule],
  templateUrl: './rfc2544-streams.component.html',
  styleUrls: ['./rfc2544-streams.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('isExpanded', style({ height: '*', visibility: 'visible' })),
      transition('isExpanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class RFC2544StreamsComponent implements OnInit, OnDestroy {
  @Input() tab: BaseSetupTab;
  @Input() componentId: string;
  @Input() stateHistory: IComponentStateHistory;
  @Input() otherTabStateHistory: IComponentStateHistory;
  // eslint-disable-next-line
  @Input() restoreAgentBasedConfig?: Function;
  // eslint-disable-next-line
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onOtherTabDataValueChange = new EventEmitter<any>();
  isValidRemoveAction = false;
  isValidCloneAction = false;
  config: any;
  parentConfig: any;
  streamConfig: any;
  outputs: Array<string>;
  maxBandWidth = 10000;
  cirMinValue = 1;
  defaultBandWidth = 10000;
  configMapping = ConfigFieldMapping;
  defaultStreamObj: any;
  tabsubscription: Subscription;
  agentSubscription: Subscription;
  readonly unsubscribe: Subject<void> = new Subject();
  vlanConfig = vlanConfigValue.UNTAGGED;
  dscpConfigTypes = DSCP_CONFIG_TYPE;
  tosDiffServData = CONFIRM_SELECTION;
  tosDiffServSelection = TOS_DIFF_SERV_SELECTION;
  ipAddressType = IPAddressValue.IP_V4;
  testMode = TestMode.LOOPBACK;
  dataSource = new MatTableDataSource();
  validateStream: any;
  streamRow = {
    isMultiSelectEnable: false,
    streamDscp: null,
    streamCir: 10,
    streamTosDiffServ: null,
    streamFlowLabel: null,
    streamTrafficClass: null,
    streamHopLimit: null,
    streamFrameSize: null,
    framesList: [],
    isValidConfig: true
  };
  errorValidationObj = {
    isValidDscp: true,
    isValidName: true,
    isValidField: true
  };
  defaultColumns = [];
  maxLineRateVaue: any;

  constructor(
    private dialog: MatDialog,
    public setupService: RFC2544SetupService,
    public formatService: FormatService
  ) {
    this.dataSource.data = [this.streamRow];
  }
  ngOnInit(): void {
    this.restoreFormValues();
    if (this.setupService.tabDataChangeEvents) {
      this.tabsubscription = this.setupService.tabDataChangeEvents.subscribe(event => {
        if (event === 2) {
          this.restoreFormValues();
          this.onDataChange();
        }
      });
      this.agentSubscription = this.setupService.agentChangeEvents.subscribe(event => {
        this.streamRow.isMultiSelectEnable = false;
      });
    }
  }

  restoreFormValues(): void {
    this.config = this.tab?.config;
    this.restoreOtherTabValues();
    this.defaultStreamObj = this.restoreFieldValues(ConfigFieldMapping.STREAM);
    this.parentConfig = this.tab?.['parent']['output'];
    this.outputs = this.config?.def?.outputs;
    const defaultStreamRow = this.streamRow;
    this.streamConfig = cloneDeep(this.restoreFieldValues(ConfigFieldMapping.STREAM));
    this.streamRow = cloneDeep(defaultStreamRow);
    const updatedFrames = [this.streamConfig[ConfigFieldMapping.FRAME_SIZE]];
    this.streamRow.streamFrameSize =
      updatedFrames ?? this.restoreFieldValues(ConfigFieldMapping.FRAME_SIZE);
    this.streamRow.framesList = this.getValidFrameSizes();
    this.streamRow.streamDscp = this.streamConfig[ConfigFieldMapping.DSCP];
    this.streamRow.streamCir = this.streamConfig[ConfigFieldMapping.CIR];
    this.streamRow.streamTosDiffServ = this.streamConfig[ConfigFieldMapping.TOS_DIFF_SERV];
    this.streamRow.streamFlowLabel = this.streamConfig[ConfigFieldMapping.FLOW_LABEL];
    this.streamRow.streamTrafficClass = this.streamConfig[ConfigFieldMapping.TRAFFIC_CLASS];
    this.streamRow.streamHopLimit = this.streamConfig[ConfigFieldMapping.HOP_LIMIT];
    this.dataSource.data = [this.streamRow];
    if (this.setupService?.getOriginatorAgent() && this.config) {
      const selectedDevice = this.setupService?.getOriginatorAgent();
      this.maxBandWidth = selectedDevice.deviceData.capabilities?.['phy-list']
        ? selectedDevice.deviceData.capabilities?.['rfc2544-list'].find(
            item => item['phy-id'] === selectedDevice.deviceData?.phyId
          )['max-bandwidth']
        : selectedDevice.deviceData?.capabilities?.rfc2544?.['max-bandwidth'];
      if (selectedDevice.deviceData.name.includes('QT') && this.maxBandWidth > 9999.3) {
        this.cirMinValue = 1;
        this.maxBandWidth = 9999.3;
      }
      if (selectedDevice.deviceData.name.includes('MAP')) {
        this.cirMinValue = 10;
      }
    }
    if (this.ipAddressType === IPAddressValue.IP_V4) {
      this.defaultColumns = IPV4_TABLE_HEADERS;
    } else {
      this.defaultColumns = IPV6_TABLE_HEADERS;
    }
  }

  getSelectedFrameSizes(): Array<number> {
    const singleVlanSize = this.restoreFieldValues(ConfigFieldMapping.FRAME_SIZE_SINGLE);
    const doubleVlanSize = this.restoreFieldValues(ConfigFieldMapping.FRAME_SIZE_DOUBLE);
    if (this.vlanConfig === vlanConfigValue.SINGLE_VLAN && singleVlanSize) {
      return singleVlanSize;
    } else if (this.vlanConfig === vlanConfigValue.Q_IN_Q && doubleVlanSize) {
      return doubleVlanSize;
    }
    return this.restoreFieldValues(ConfigFieldMapping.FRAME_SIZE);
  }

  getValidFrameSizes(): Array<number> {
    const singleVlanSize = this.restoreFieldValues(ConfigFieldMapping.VALID_FRAME_SIZE_SINGLE);
    const doubleVlanSize = this.restoreFieldValues(ConfigFieldMapping.VALID_FRAME_SIZE_DOUBLE);
    if (this.vlanConfig === vlanConfigValue.SINGLE_VLAN && singleVlanSize) {
      return singleVlanSize;
    } else if (this.vlanConfig === vlanConfigValue.Q_IN_Q && doubleVlanSize) {
      return doubleVlanSize;
    }
    return this.restoreFieldValues(ConfigFieldMapping.VALID_FRAME_SIZE);
  }

  restoreOtherTabValues(): void {
    this.vlanConfig = this.restoreAdvancedFieldValues(ConfigFieldMapping.VLAN_CONFIG);
    this.ipAddressType = this.restoreFieldValues(ConfigFieldMapping.IP_ADDRESS_TYPE);
    this.testMode = this.restoreFieldValues(ConfigFieldMapping.TEST_MODE);
    this.maxLineRateVaue = this.restoreFieldValues(ConfigFieldMapping.BACK_TO_BACK_MAX_LINE_RATE);
  }

  //eslint-disable-next-line
  restoreAdvancedFieldValues(field: string): any {
    const data = this.otherTabStateHistory('rfc2544.endpoints.html', field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }
  // eslint-disable-next-line
  onDataChange(value?: number, index?: number, name?: string, element?: any): void {
    this.validateStream = this.streamRow['isValidConfig'];
    if (name) {
      this.streamConfig[name] = value;
    }
    if (!this.validateStream) {
      this.onDataEmit(ConfigFieldMapping.STREAM, this.streamConfig, false);
    } else {
      this.onDataEmit(ConfigFieldMapping.STREAM, this.streamConfig, true);
    }
  }

  // eslint-disable-next-line
  onClickFrameSize(element: any, index: number): void {
    if (this.vlanConfig !== vlanConfigValue.UNTAGGED) {
      element.framesList[this.ipAddressType]['selectedVlans'] =
        this.vlanConfig === vlanConfigValue.Q_IN_Q ? 2 : 1;
    }
    const frameDataObj = {
      rowData: element.framesList[this.ipAddressType],
      sctFrames: this.updateFrames(element),
      sptFrames: this.updateFrames(element),
      multiSelectFrames: false,
      emixFrames: false,
      isMultiSelectEnable: false,
      multiSelectFramesEnabled: true
    };
    const dialogRef = this.dialog.open(FrameSizeSelectorComponent, {
      data: frameDataObj,
      disableClose: true
    });
    dialogRef.beforeClosed().subscribe(_res => {
      if (_res) {
        element.streamFrameSize =
          _res?.sctFrames && _res?.sctFrames !== null ? _res?.sctFrames : _res?.sptFrames;
        element.isMultiSelectEnable = false;
        this.onDataEmit(ConfigFieldMapping.FRAME_SIZE, element.streamFrameSize, true);
        this.onDataChange(element.streamFrameSize, index, ConfigFieldMapping.FRAME_SIZE, element);
      }
    });
  }
  // eslint-disable-next-line
  updateFrames(rowData: any): any {
    const frames = rowData.streamFrameSize;
    const frameList = rowData.framesList[this.parentConfig?.['ip-address-type']]['frameList'];
    let index = 0;
    const isEmix = false;
    frameList?.forEach(item => {
      index = index + 1;
      if (!isEmix) {
        if (frames.toString().split(',').includes(item.label.toString())) {
          frameList[index - 1]['enable'] = true;
        } else if (item.label.toString() === 'custom' && frames.indexOf('custom') !== -1) {
          frameList[index - 1]['enable'] = true;
          frameList[index - 1]['value'] = rowData.streamFrameSize
            .split('custom:')[1]
            .replace(',', '');
        } else {
          frameList[index - 1]['enable'] = false;
        }
      }
    });
    return frameList;
  }
  // eslint-disable-next-line
  checkValidation(field: string, value: any, element?: any): any {
    const errMsg = { isValid: false, message: '' };
    if (
      field === 'cir' &&
      (value === null || value < this.cirMinValue || value > this.maxBandWidth)
    ) {
      errMsg.isValid = true;
      errMsg.message =
        'Valid range is number between ' + this.cirMinValue + ' and ' + this.maxBandWidth;
      this.errorValidationObj.isValidField = false;
    } else if (field === 'cir') {
      this.errorValidationObj.isValidField = true;
    }
    if (field === 'cir' && this.maxLineRateVaue < value) {
      errMsg.isValid = true;
      errMsg.message = 'Subscriber Rate/CIR cannot be more than  Maximum line rate';
      this.errorValidationObj.isValidField = false;
    }
    if (field === 'flowLabel') {
      const reg1 = '[^A-Fa-f0-9]{1,5}';
      const flow_label_dec = parseInt(value, 16);
      if (value == '' || value?.match(reg1) != null || flow_label_dec == 0) {
        errMsg.isValid = true;
        errMsg.message = 'Flow Label must be entered as a hex number between 1 and FFFFF';
        this.errorValidationObj.isValidField = false;
      } else if (element?.streamFlowLabel) {
        element.streamFlowLabel = value.toUpperCase();
        this.errorValidationObj.isValidField = true;
      }
    }
    if (field === 'trafficClass') {
      const reg1 = '[^A-Fa-f0-9]{1,2}';
      if (value == '' || value?.match(reg1) != null) {
        errMsg.isValid = true;
        errMsg.message = 'Traffic class must be entered as a hex number between 0 and FF';
        this.errorValidationObj.isValidField = false;
      } else if (element?.streamTrafficClass) {
        element.streamTrafficClass = value.toUpperCase();
        this.errorValidationObj.isValidField = true;
      }
    }
    if (field === 'hopLimit') {
      if (value == null || value < 1 || value > 255) {
        errMsg.isValid = true;
        errMsg.message = 'Valid range is a whole number between 1 and 255';
        this.errorValidationObj.isValidField = false;
      } else {
        this.errorValidationObj.isValidField = true;
      }
    }
    if (element) {
      if (this.errorValidationObj.isValidField) {
        element.isValidConfig = true;
      } else {
        element.isValidConfig = false;
      }
    }
    this.onDataChange();
    return errMsg;
  }

  // eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
  }
}
